import { useMount } from 'ahooks';
import { Form, Input, Select, Table, TableColumnsType, Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxSkeleton } from 'components';
import { AddIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import {
  DEFAULT_DATE_FORMAT,
  NOTE_DATE_FORMAT,
  RevxDate,
} from 'components/RevxDate';
import { Text } from 'components/RevxTypography';
import {
  JobNotesCategoryEnum,
  JobNotesItem,
} from 'domains/workspaceDetailDomain';
import { JobItem } from 'domains/workspaceDomain';
import NameBadge from 'features/Workspace/components/NameBadge';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './index.less';

const { Option } = Select;

const CommonNotes = (jobData: JobItem) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [noteList, setNoteList] = useState<JobNotesItem[]>([]);
  const [addNoteStatus, setAddNoteStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const selectCatrgoy = [
    { name: 'Approval', value: JobNotesCategoryEnum.APPROVAL },
    { name: 'Configuration', value: JobNotesCategoryEnum.CONFIGURATION },
    { name: 'Price', value: JobNotesCategoryEnum.PRICE },
    { name: 'Proof', value: JobNotesCategoryEnum.PROOF },
    { name: 'Job Files', value: JobNotesCategoryEnum.JOB_FILES },
    { name: 'Shipping', value: JobNotesCategoryEnum.SHIPPING },
    { name: 'Other', value: JobNotesCategoryEnum.OTHER },
    {
      name: 'Art File Instructions',
      value: JobNotesCategoryEnum.ART_FILE_INSTRUCTIONS,
    },
    {
      name: 'Shipping Instructions',
      value: JobNotesCategoryEnum.SHIPPING_INSTRUCTIONS,
    },
  ];
  const isLoadingNote = useAppSelector(
    (state) => state.loading.effects.workspaceDetail.saveNote
  );
  const defaultNote = useCallback(async () => {
    setLoading(true);
    const noteRes = await dispatch.workspaceDetail.getNotes(jobData);
    setNoteList(noteRes);
    setLoading(false);
    return noteRes;
  }, [dispatch.workspaceDetail, jobData, setLoading]);
  useMount(async () => {
    defaultNote();
    dispatch.workspace.getJobItemCount(jobData.id);
  });
  const columns: TableColumnsType<any> = [
    {
      title: t('workspace.detail.notes.date'),
      dataIndex: 'createAt',
      key: 'id',
      render: (text) => (
        <RevxDate dateStr={text} formatStr={DEFAULT_DATE_FORMAT} />
      ),
      width: 100,
    },
    {
      title: t('workspace.detail.notes.time'),
      dataIndex: 'createAt',
      key: 'id',
      render: (text) => (
        <RevxDate dateStr={text} formatStr={NOTE_DATE_FORMAT} />
      ),
      width: 100,
    },
    {
      title: t('workspace.detail.notes.from'),
      dataIndex: 'creatorFullName',
      key: 'id',
      render: (text, record) => (
        <div className="table-tip">
          <NameBadge name={text || ''} />
          <Tooltip title={text} className="table-tip-text">
            <Text
              ellipsis
              style={{
                width: 90,
              }}
            >
              {text}
            </Text>
          </Tooltip>
        </div>
      ),
      width: 130,
    },
    {
      title: t('workspace.detail.notes.category'),
      dataIndex: 'category',
      key: 'id',
      render: (text) => (
        <>{selectCatrgoy.filter((item) => item.value === text)[0]?.name}</>
      ),
      width: 100,
    },
    {
      title: t('workspace.detail.notes.notes'),
      dataIndex: 'content',
      key: 'id',
      render: (text, record) => (
        <div className="table-tip">
          <Tooltip title={text}>
            <Text
              ellipsis
              style={{
                maxWidth: 650,
              }}
            >
              {text}
            </Text>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="common-notes-card">
      <RevxSkeleton loading={loading}>
        <Table
          columns={columns}
          dataSource={noteList}
          pagination={false}
          size="small"
        />

        <div className="common-notes-card-action" hidden={addNoteStatus}>
          <RevxButton
            style={{ borderRadius: 50 }}
            type="primary"
            onClick={() => setAddNoteStatus(!addNoteStatus)}
          >
            <AddIcon />
            {t('workspace.detail.notes.addNote')}
          </RevxButton>
        </div>
        <div className="common-notes-card-edit" hidden={!addNoteStatus}>
          <Form form={form}>
            <div className="common-notes-card-edit-action">
              <Form.Item
                name="category"
                style={{ marginBottom: 0 }}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  style={{ width: 200, height: 28 }}
                  placeholder={t(
                    'workspace.detail.notes.selectCategoryPlaceholder'
                  )}
                  optionFilterProp="children"
                  getPopupContainer={(e) => {
                    return e as HTMLElement;
                  }}
                >
                  {selectCatrgoy.map((item) => (
                    <Option value={item.value} key={item.value + item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <div>
                <RevxButton
                  type="ghost"
                  onClick={async () => {
                    setAddNoteStatus(!addNoteStatus);
                    form.resetFields();
                  }}
                >
                  {t('common.cancel')}
                </RevxButton>
                <RevxButton
                  type="primary"
                  loading={isLoadingNote}
                  onClick={async () => {
                    // form.validateFields(res => )
                    try {
                      const formRes = await form.validateFields();
                      await dispatch.workspaceDetail.saveNote({
                        ...formRes,
                        jobId: jobData.id,
                      });
                      form.resetFields();
                      await defaultNote();
                      setAddNoteStatus(!addNoteStatus);
                      dispatch.workspace.getJobItemCount(jobData.id);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                >
                  {t('workspace.detail.notes.attachNoteButton')}
                </RevxButton>
              </div>
            </div>
            <Form.Item
              name="content"
              rules={[
                { required: true },
                {
                  max: 1024,
                  message: t('workspace.detail.notes.errorMessage'),
                },
              ]}
            >
              <Input.TextArea
                className="common-notes-card-edit-input"
                placeholder={t('workspace.detail.notes.editMessagePlaceholder')}
                autoSize={{ minRows: 3, maxRows: 5 }}
              />
            </Form.Item>
          </Form>
        </div>
      </RevxSkeleton>
    </div>
  );
};

export default CommonNotes;
